/**
 * Admin → Waitlist. BETA-ONLY.
 *
 * DELETE THIS FILE when beta mode ends, along with `use-waitlist-entries.ts`,
 * `use-waitlist-stats.ts`, the `waitlist-invite` edge function and the dashboard
 * card that links here. The route is registered with `href: null` and reached
 * only from that card, which is itself behind `isBetaMode()`.
 *
 * WHY IT EXISTS: the beta is invite-only — `AuthProvider.signUp` refuses any
 * email whose waitlist status is not `invited` — and nothing in the product
 * could set that status, so the gate had no key. This is the key, plus the
 * numbers needed to decide who gets one.
 *
 * The counts are deliberately per-ROLE rather than a single total: the point of
 * the invite gate is controlling how many investors are in the room relative to
 * startups, and a combined number hides exactly that.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Redirect } from 'expo-router';
import { UserPlus, Rocket, TrendingUp, CheckCircle2, Clock, MailWarning } from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useAlert } from '@/components/AlertDialog';
import { getUserMessage } from '@/lib/errors';
import { isBetaMode } from '@/lib/config/app-mode';
import {
  useWaitlistEntries,
  useInviteFromWaitlist,
  type WaitlistEntry,
  type WaitlistRole,
  type WaitlistStatus,
} from '@/lib/admin/use-waitlist-entries';
import { APP_LOCALE } from '@/lib/format';

type RoleFilter = WaitlistRole | 'all';
type StatusFilter = WaitlistStatus | 'all';

const ROLE_FILTERS: { label: string; value: RoleFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Startups', value: 'startup' },
  { label: 'Investors', value: 'investor' },
  { label: 'Undecided', value: 'undecided' },
];

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Waiting', value: 'waiting' },
  { label: 'Invited', value: 'invited' },
  { label: 'Joined', value: 'converted' },
];

export default function AdminWaitlistScreen() {
  const colors = useThemeColors();
  const { success: showSuccess, error: showError, confirm } = useAlert();
  const { data: entries = [], isLoading, isError, error } = useWaitlistEntries();
  const invite = useInviteFromWaitlist();

  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('waiting');
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Counts are computed over EVERY entry, never the filtered view — they are
  // the decision aid, so they must not move when the filters do.
  const counts = useMemo(() => {
    const tally = (role: WaitlistRole) => ({
      waiting: entries.filter((e) => e.role === role && e.status === 'waiting').length,
      invited: entries.filter((e) => e.role === role && e.status === 'invited').length,
      joined: entries.filter((e) => e.role === role && e.status === 'converted').length,
    });
    return { startup: tally('startup'), investor: tally('investor'), undecided: tally('undecided') };
  }, [entries]);

  const visible = useMemo(
    () =>
      entries.filter(
        (e) =>
          (roleFilter === 'all' || e.role === roleFilter) &&
          (statusFilter === 'all' || e.status === statusFilter),
      ),
    [entries, roleFilter, statusFilter],
  );

  // Belt and braces: the card that links here is already beta-gated, but a
  // stale deep link should not open an empty screen once beta ends.
  if (!isBetaMode()) return <Redirect href="/(admin)/admin-dashboard" />;

  const handleInvite = async (entry: WaitlistEntry) => {
    const who = entry.full_name?.trim() || entry.email;
    const resend = entry.status === 'invited';

    const ok = await confirm(
      resend ? 'Send the invitation again?' : `Invite ${who}?`,
      resend
        ? `${who} was already invited${entry.invited_at ? ` on ${new Date(entry.invited_at).toLocaleDateString(APP_LOCALE)}` : ''}. This sends the same email again and does not change when they were invited.`
        : `They will be emailed straight away and can then create an account with ${entry.email}. This cannot be undone from here.`,
    );
    if (!ok) return;

    setPendingId(entry.id);
    invite.mutate(entry.id, {
      onSuccess: (result) => {
        setPendingId(null);
        if (result.emailSent) {
          showSuccess(
            result.alreadyInvited ? 'Invitation re-sent' : 'Invited',
            `${who} can now create an account.`,
          );
        } else {
          // The invitation is real even though the mail failed — say both halves,
          // or they look identical and the admin never follows up.
          showError(
            'Invited, but the email failed',
            `${who} is marked as invited and can sign up, but the email did not send${result.emailError ? ` (${result.emailError})` : ''}. Reach them another way, or use Send again.`,
          );
        }
      },
      onError: (err) => {
        setPendingId(null);
        showError('Could not invite', getUserMessage(err));
      },
    });
  };

  return (
    <AppLayout
      title="Waitlist"
      breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Waitlist' }]}
    >
      <SafeAreaView
        className="flex-1"
        style={{ backgroundColor: colors.surface.secondary }}
        edges={isWeb ? [] : ['top', 'bottom']}
      >
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
          {/* Heading */}
          <View className="px-1 pt-2 pb-3">
            <View className="flex-row items-center">
              <UserPlus size={18} color={colors.text.primary} />
              <Text style={{ color: colors.text.primary }} className="text-xl font-bold ml-2" numberOfLines={1}>
                Waitlist
              </Text>
              <View className="px-2 py-0.5 rounded-full ml-2" style={{ backgroundColor: '#FF950015' }}>
                <Text style={{ color: '#FF9500' }} className="text-xs font-semibold">Beta</Text>
              </View>
            </View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
              Inviting someone is the only way they can create an account while the beta is
              invite-only. They are emailed immediately.
            </Text>
          </View>

          {/* Who is in the room — the reason the gate exists */}
          <View className="flex-row gap-2 mb-3">
            <RoleCount
              label="Startups"
              icon={<Rocket size={13} color="#0D9488" />}
              tint="#0D9488"
              counts={counts.startup}
            />
            <RoleCount
              label="Investors"
              icon={<TrendingUp size={13} color="#3B82F6" />}
              tint="#3B82F6"
              counts={counts.investor}
            />
          </View>

          {/* Filters */}
          <FilterRow options={ROLE_FILTERS} value={roleFilter} onChange={setRoleFilter} />
          <FilterRow options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />

          {isLoading && (
            <View className="items-center py-10">
              <ActivityIndicator color={colors.primary[600]} />
            </View>
          )}

          {isError && (
            <Text style={{ color: colors.error }} className="text-sm py-6 text-center">
              {getUserMessage(error)}
            </Text>
          )}

          {!isLoading && !isError && visible.length === 0 && (
            <Text style={{ color: colors.text.tertiary }} className="text-sm py-10 text-center">
              Nobody matches this filter.
            </Text>
          )}

          {visible.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              busy={pendingId === entry.id}
              onInvite={() => handleInvite(entry)}
            />
          ))}
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------

function RoleCount({
  label,
  icon,
  tint,
  counts,
}: {
  label: string;
  icon: React.ReactNode;
  tint: string;
  counts: { waiting: number; invited: number; joined: number };
}) {
  const colors = useThemeColors();
  return (
    <View className="flex-1 rounded-2xl p-3" style={{ backgroundColor: colors.surface.primary }}>
      <View className="flex-row items-center gap-1.5 mb-2">
        {icon}
        <Text style={{ color: colors.text.primary }} className="text-sm font-semibold" numberOfLines={1}>
          {label}
        </Text>
      </View>
      <View className="flex-row items-end gap-3 flex-wrap">
        <View>
          <Text style={{ color: tint }} className="text-lg font-bold">{counts.joined}</Text>
          <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>joined</Text>
        </View>
        <View>
          <Text style={{ color: colors.text.primary }} className="text-lg font-bold">{counts.invited}</Text>
          <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>invited</Text>
        </View>
        <View>
          <Text style={{ color: colors.text.tertiary }} className="text-lg font-bold">{counts.waiting}</Text>
          <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>waiting</Text>
        </View>
      </View>
    </View>
  );
}

function FilterRow<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const colors = useThemeColors();
  return (
    <View className="flex-row flex-wrap gap-2 mb-2">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <AnimatedPressable
            key={`filter-${opt.value}`}
            onPress={() => onChange(opt.value)}
            className="px-3 py-1.5 rounded-full"
            style={{
              backgroundColor: active ? colors.primary[600] : colors.surface.primary,
            }}
          >
            <Text
              className="text-xs font-semibold"
              style={{ color: active ? '#FFFFFF' : colors.text.secondary }}
              numberOfLines={1}
            >
              {opt.label}
            </Text>
          </AnimatedPressable>
        );
      })}
    </View>
  );
}

function EntryRow({
  entry,
  busy,
  onInvite,
}: {
  entry: WaitlistEntry;
  busy: boolean;
  onInvite: () => void;
}) {
  const colors = useThemeColors();
  const joined = entry.status === 'converted';
  const invited = entry.status === 'invited';

  const roleTint =
    entry.role === 'investor' ? '#3B82F6' : entry.role === 'startup' ? '#0D9488' : colors.text.tertiary;

  return (
    <View
      className="rounded-2xl border p-4 mb-2"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
    >
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1">
          <View className="flex-row items-center gap-2 flex-wrap">
            <Text style={{ color: colors.text.primary }} className="text-base font-semibold" numberOfLines={1}>
              {entry.full_name?.trim() || entry.email}
            </Text>
            <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: `${roleTint}15` }}>
              <Text style={{ color: roleTint }} className="text-xs font-semibold" numberOfLines={1}>
                {entry.role}
              </Text>
            </View>
            {!entry.verified && (
              <View className="flex-row items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors.warning}15` }}>
                <MailWarning size={11} color={colors.warning} />
                <Text style={{ color: colors.warning }} className="text-xs font-semibold" numberOfLines={1}>
                  unverified
                </Text>
              </View>
            )}
          </View>

          {!!entry.full_name?.trim() && (
            <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5" numberOfLines={1}>
              {entry.email}
            </Text>
          )}

          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1" numberOfLines={1}>
            #{entry.position}
            {entry.company_name ? ` · ${entry.company_name}` : ''}
            {entry.title ? ` · ${entry.title}` : ''}
            {entry.referral_count > 0 ? ` · ${entry.referral_count} referrals` : ''}
          </Text>
        </View>

        <View className="items-end">
          {joined ? (
            <View className="flex-row items-center gap-1">
              <CheckCircle2 size={14} color={colors.success} />
              <Text style={{ color: colors.success }} className="text-xs font-semibold" numberOfLines={1}>
                Joined
              </Text>
            </View>
          ) : (
            <View
              style={{
                height: 34,
                borderRadius: 10,
                overflow: 'hidden',
                backgroundColor: invited ? colors.surface.secondary : colors.primary[600],
                opacity: busy ? 0.6 : 1,
              }}
            >
              <AnimatedPressable
                onPress={onInvite}
                disabled={busy}
                style={{ flex: 1, paddingHorizontal: 14, alignItems: 'center', justifyContent: 'center' }}
              >
                {busy ? (
                  <ActivityIndicator size="small" color={invited ? colors.text.secondary : '#FFFFFF'} />
                ) : (
                  <Text
                    className="text-xs font-semibold"
                    style={{ color: invited ? colors.text.secondary : '#FFFFFF' }}
                    numberOfLines={1}
                  >
                    {invited ? 'Send again' : 'Invite'}
                  </Text>
                )}
              </AnimatedPressable>
            </View>
          )}

          {invited && !!entry.invited_at && (
            <View className="flex-row items-center gap-1 mt-1">
              <Clock size={10} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                {new Date(entry.invited_at).toLocaleDateString(APP_LOCALE)}
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}
